"use client"
import { useState, useEffect, useRef } from "react";
import { Button } from "@heroui/react";
import { Textarea } from "@heroui/react";
import useLogin from "@/hooks/useLogin";
import EmojiKeyboard from "./emoji";
import { Smile, X } from "lucide-react";
// import ImageUploaderModal_V3 from "../ImageUploaderModal_V3";
import { useUserInfoStore } from "@/models/userInfo";
import clsx from "clsx";

// New component for image thumbnail previews
const ImageThumbnailPreview = ({ images, onRemove }: { images: string[]; onRemove: (index: number) => void }) => {
    return (
        <div className="flex space-x-2 mt-2">
            {images.map((image, index) => (
                <div key={index} className="relative">
                    <img src={image} alt={`preview ${index}`} className="w-20 h-20 rounded object-cover" />
                    <button
                        onClick={() => onRemove(index)}
                        className="absolute top-0 right-0 bg-black/50 text-white rounded-full p-0.5"
                    >
                        <X size={14} />
                    </button>
                </div>
            ))}
        </div>
    );
};

const CommentInput = ({ postComment, isMobile = false, slot }: { postComment: (comment: string) => void, isMobile?: boolean, slot?: React.ReactNode }) => {
    const [comment, setComment] = useState("");
    const { checkLogin } = useLogin();
    const [focus, setFocus] = useState(false);
    // const [isUploaderOpen, setIsUploaderOpen] = useState(false);
    const [images, setImages] = useState<string[]>([]);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const { userInfo } = useUserInfoStore();
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handlePostComment = () => {
        postComment(comment);
        setComment("");
        setImages([]);
        setFocus(false);
    }

    // focus后光标在最后
    useEffect(() => {
        if (focus && textareaRef.current) {
            // 延迟focus，确保键盘有时间弹出
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
                }
            }, 100);
        }
    }, [focus]);

    const [emojiList, setEmojiList] = useState<string[]>([]);
    useEffect(() => {
        setEmojiList(getRecentEmojis());
    }, []);
    //点击回复评论后100ms自动focus 
    useEffect(() => {
        if (focus) {
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 300);
        }
    }, [focus]);
    return (
        <div
            ref={containerRef}
            className="p-4 border-t border-gray-200 bg-white z-10 transition-transform duration-300"
            style={{
                // transform: getTransform(),
                bottom: '0px'
            }}
        >
            {focus ? (
                <div className="flex flex-col space-y-3 max-w-[900px] mx-auto">
                    <Textarea
                        ref={textareaRef}
                        placeholder="Write a comment..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="bg-transparent border-0 text-gray-900 placeholder-gray-500 focus:ring-0 min-h-[80px] selectable"
                        style={{
                            fontSize: '16px', // 防止移动端浏览器自动放大
                            lineHeight: '1.5'
                        }}
                        onFocus={() => {
                            // 确保focus状态正确
                            if (!focus) setFocus(true);
                        }}
                    />
                    {images.length > 0 && (
                        <ImageThumbnailPreview
                            images={images}
                            onRemove={(index) => {
                                setImages(images.filter((_, i) => i !== index));
                            }}
                        />
                    )}
                    {isMobile && <div className="w-full flex justify-around text-xl">
                        {MOBILE_EMOJI_LIST.map((emoji: string) => (
                            <span
                                key={emoji}
                                className="cursor-pointer hover:scale-110 transition-transform"
                                onClick={(e) => {
                                    checkLogin(() => {
                                        e.stopPropagation();
                                        setComment(emoji);
                                        setFocus(true);
                                    })();
                                }}
                            >
                                {emoji}
                            </span>
                        ))}
                    </div>}
                    <div className="flex justify-between items-center">
                        <div className="flex space-x-4 items-center">
                            <EmojiKeyboard
                                onEmojiSelect={(emoji) => {
                                    setComment(comment + emoji);
                                }}
                            >
                                <button className="text-gray-600 hover:text-gray-900">
                                    <Smile size={20} />
                                </button>
                            </EmojiKeyboard>
                            {/* <button className="text-gray-600 hover:text-gray-900" onClick={() => setIsUploaderOpen(true)}>
                                <Image size={20} />
                            </button> */}
                        </div>
                        <div className="flex space-x-2">
                            <Button
                                onPress={() => {
                                    setFocus(false);
                                    setIsKeyboardVisible(false);
                                    setKeyboardHeight(0);
                                }}
                                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </Button>
                            <Button
                                onPress={checkLogin(handlePostComment)}
                                disabled={!comment.trim() && images.length === 0}
                                className="px-4 py-2 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Post
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex space-x-3 max-w-[900px] justify-between items-center">
                    <div
                        className="flex flex-1 space-x-3 max-w-[900px] items-center bg-gray-100 p-2 rounded-full cursor-text"
                        onClick={checkLogin(() => { setFocus(true) })}
                    >
                        {userInfo.profile_url &&
                            <img
                                src={userInfo.profile_url}
                                alt="Your avatar"
                                width={32}
                                height={32}
                                className="w-8 h-8 rounded-full object-cover"
                            />}
                        <div className="flex-1 text-sm text-gray-600 w-full">
                            write a comment...
                        </div>
                        <div className={clsx("flex space-x-2 text-xl", isMobile ? "hidden" : "")}>
                            {emojiList.map((emoji: string) => (
                                <span
                                    key={emoji}
                                    className="cursor-pointer hover:scale-110 transition-transform"
                                    onClick={(e) => {
                                        checkLogin(() => {
                                            e.stopPropagation();
                                            setComment(emoji);
                                            setFocus(true);
                                        })();
                                    }}
                                >
                                    {emoji}
                                </span>
                            ))}
                        </div>
                    </div>
                    {slot && slot}
                </div>
            )}
        </div>
    );
};

export default CommentInput;

function getRecentEmojis(): string[] {
    const recentEmojis = localStorage.getItem("recentEmojis");
    if (recentEmojis) {
        return JSON.parse(recentEmojis).slice(0, 3);
    }
    return ["🥰", "🤪", "👍"];
}

const MOBILE_EMOJI_LIST = [
    "😆", // 笑
    "🥰", // 亲亲
    "😮", // 惊讶
    "🤔", // 思考
    "👍🏻", // 赞
    "🙏🏻", // 合十
    "🌸", // 花
    "💖", // 爱心
];